import {
  adminUserRowSchema,
  allowListRowSchema,
  auditRowSchema,
  type AdminUserRow,
  type AllowListRow,
  type AuditRow,
  type CreateAllowListEntryRequest,
  type UpdateAllowListEntryRequest,
} from "@shared/schemas/admin";
import { collectionSchema, type Collection } from "@shared/schemas/common";
import { apiDelete, apiGet, apiPatch, apiPost, queryString } from "@client/lib/api";

/** The admin half of docs/06-api.md, typed by the schemas the server validates with. */

const allowListCollectionSchema = collectionSchema(allowListRowSchema);
const adminUserCollectionSchema = collectionSchema(adminUserRowSchema);
const auditCollectionSchema = collectionSchema(auditRowSchema);

export interface AllowListFilters {
  q?: string;
  includeRevoked?: boolean;
  limit?: number;
  offset?: number;
}

export const listAllowList = (filters: AllowListFilters = {}): Promise<Collection<AllowListRow>> =>
  apiGet(
    `/admin/allowlist${queryString({
      q: filters.q,
      includeRevoked: filters.includeRevoked ? "true" : undefined,
      limit: filters.limit,
      offset: filters.offset,
    })}`,
    allowListCollectionSchema,
  );

export const createAllowListEntry = (input: CreateAllowListEntryRequest): Promise<AllowListRow> =>
  apiPost("/admin/allowlist", allowListRowSchema, input);

export const updateAllowListEntry = (
  id: string,
  input: UpdateAllowListEntryRequest,
): Promise<AllowListRow> =>
  apiPatch(`/admin/allowlist/${encodeURIComponent(id)}`, allowListRowSchema, input);

/**
 * Answers with the revoked row rather than a 204, so the table can show the
 * entry greying out instead of making a second request to find out what it did.
 */
export const revokeAllowListEntry = (id: string): Promise<AllowListRow> =>
  apiDelete(`/admin/allowlist/${encodeURIComponent(id)}`, allowListRowSchema);

export interface AdminUserFilters {
  q?: string;
  limit?: number;
  offset?: number;
}

export const listAdminUsers = (filters: AdminUserFilters = {}): Promise<Collection<AdminUserRow>> =>
  apiGet(
    `/admin/users${queryString({ q: filters.q, limit: filters.limit, offset: filters.offset })}`,
    adminUserCollectionSchema,
  );

export interface AuditFilters {
  actorId?: string;
  action?: string;
  from?: string;
  to?: string;
  limit?: number;
  offset?: number;
}

export const listAuditLog = (filters: AuditFilters = {}): Promise<Collection<AuditRow>> =>
  apiGet(
    `/admin/audit${queryString({
      actorId: filters.actorId,
      action: filters.action,
      from: filters.from,
      to: filters.to,
      limit: filters.limit,
      offset: filters.offset,
    })}`,
    auditCollectionSchema,
  );
